import React from 'react';
import { useTranslation } from 'react-i18next';
import { Logo } from '../../../../../components/app/logo/logo';
import { IconDashboard } from '../../../../../components/app/icons/dashboard';
import { IconMenu } from '../../../../../components/app/icons/menu';
import { IconArrowDown } from '../../../../../components/app/icons/arrow-down';
import { IconHelpFilled } from '../../../../../components/app/icons/help-filled';
import { IconWallet } from '../../../../../components/app/icons/wallet';
import { IconInvestment } from '../../../../../components/app/icons/investment';
import { IconProfits } from '../../../../../components/app/icons/profits';
import { IconTrades } from '../../../../../components/app/icons/trades';
import { IconReport } from '../../../../../components/app/icons/report';
import { IconDividends } from '../../../../../components/app/icons/dividends';
import { cn } from '../../../../../utils/tailwind';
import styles from './hero-application.module.css';

interface MetricCardProps {
    area: string;
    label: string;
    children: React.ReactNode;
    blue?: boolean;
    order: number;
}

const MetricCard: React.FC<MetricCardProps> = ({ area, label, children, blue = false, order }) => (
    <div
        className={cn(styles.card, blue && styles.blue)}
        style={
            {
                gridArea: area,
                '--draw-delay': `calc((${2.7 + order * 0.24}s - var(--hero-app-lead)) * var(--hero-motion-scale))`,
            } as React.CSSProperties
        }
    >
        <svg className={styles.cardOutline} aria-hidden="true">
            <rect x="0.75" y="0.75" rx="10" pathLength="1" />
        </svg>
        <div className={styles.cardLabel}>
            {label}
            <span className={styles.help}>
                <IconHelpFilled />
            </span>
        </div>
        {children}
    </div>
);

const Allocation: React.FC<{ title: string; labels: string[]; values: string[] }> = ({ title, labels, values }) => (
    <div className={styles.allocation}>
        <div className={styles.cardLabel}>{title}</div>
        <div className={styles.allocationBody}>
            <svg viewBox="0 0 100 100" className={styles.donut} aria-hidden="true">
                <circle cx="50" cy="50" r="34" fill="none" strokeWidth="17" />
                <circle
                    cx="50"
                    cy="50"
                    r="34"
                    fill="none"
                    strokeWidth="17"
                    strokeDasharray="145 214"
                    transform="rotate(-90 50 50)"
                />
                <circle cx="50" cy="50" r="20" fill="none" stroke="#ececf0" strokeWidth="0.5" />
            </svg>
            <div className={styles.legend}>
                {labels.map((label, index) => (
                    <div key={label}>
                        <i />
                        <span>{label}</span>
                        <strong>{values[index]}</strong>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

/** Static, explicitly labelled sample data; never connected to the visitor's account. */
export const HeroApplication: React.FC = () => {
    const { t } = useTranslation();
    const copy = (key: string) => t(`pages.homepage.heroApp.${key}`);
    const navigation = [
        { key: 'overview', icon: <IconDashboard /> },
        { key: 'assets', icon: <IconTrades /> },
        { key: 'investments', icon: <IconInvestment /> },
        { key: 'performance', icon: <IconProfits /> },
        { key: 'dividends', icon: <IconDividends /> },
        { key: 'taxReport', icon: <IconReport /> },
    ];

    return (
        <div className={styles.application}>
            <div className={styles.appHeader}>
                <div className={styles.logo}>
                    <div className={styles.logoOutline} aria-hidden="true">
                        <Logo gradientId="hero-app-outline" />
                    </div>
                    <div className={styles.logoColor}>
                        <Logo gradientId="hero-app-color" />
                    </div>
                </div>
                <span className={styles.account}>
                    <span className={styles.accountIcon}>
                        <IconWallet />
                    </span>
                    <span>{copy('summary')}</span>
                    <span className={styles.accountArrow}>
                        <IconArrowDown />
                    </span>
                </span>
                <div className={styles.navigation}>
                    {navigation.map(({ key, icon }, index) => (
                        <span key={key} className={index === 0 ? styles.active : undefined}>
                            {icon}
                            <span>{copy(key)}</span>
                        </span>
                    ))}
                </div>
                <span className={styles.mobileMenu} aria-hidden="true">
                    <IconMenu />
                </span>
            </div>
            <div className={styles.tabs}>
                <span className={styles.selectedTab}>{copy('stocks')}</span>
                <span>{copy('personalFinance')}</span>
                <span>Investown</span>
                <span>{copy('all')}</span>
            </div>
            <div className={styles.content}>
                <div className={styles.headingRow}>
                    <div className={styles.heading}>
                        {copy('overview')} <span>/ {copy('stocks')}</span>
                    </div>
                    <span className={styles.sample}>{copy('sample')}</span>
                </div>
                <div className={styles.metrics}>
                    <MetricCard area="value" label={copy('portfolioValue')} blue order={0}>
                        <div className={styles.summary}>
                            <IconWallet />
                            {copy('summary')}
                        </div>
                        <div className={styles.bottom}>
                            <strong className={styles.amount}>1 245 800 Kč</strong>
                            <span className={styles.note}>{copy('updated')}</span>
                        </div>
                    </MetricCard>
                    <MetricCard area="realized" label={copy('realized')} order={1}>
                        <div className={styles.bottom}>
                            <strong className={cn(styles.amount, styles.positive)}>84 520 Kč</strong>
                            <span className={styles.note}>{copy('portfolioShare')}</span>
                        </div>
                    </MetricCard>
                    <MetricCard area="dividends" label={copy('dividends')} order={2}>
                        <div className={styles.dividendValues}>
                            <div>
                                <strong>2,70 %</strong>
                                <span>yield</span>
                            </div>
                            <div>
                                <span>{copy('prediction')}</span>
                                <strong>33 636 Kč</strong>
                            </div>
                            <div>
                                <span>{copy('paid')}</span>
                                <strong>28 460 Kč</strong>
                            </div>
                        </div>
                    </MetricCard>
                    <MetricCard area="unrealized" label={copy('unrealized')} order={3}>
                        <div className={styles.bottom}>
                            <strong className={cn(styles.amount, styles.positive)}>137 280 Kč</strong>
                            <div className={styles.return}>
                                <span>+12,4 %</span>
                                <span>
                                    SR <b>TWR</b> MWR
                                </span>
                            </div>
                        </div>
                    </MetricCard>
                    <MetricCard area="invested" label={copy('invested')} order={4}>
                        <div className={styles.bottom}>
                            <strong className={styles.amount}>1 108 520 Kč</strong>
                            <span className={styles.note}>{copy('assetsCount')}</span>
                            <span className={styles.note}>
                                {copy('duration')}
                                <br />
                                <b>36 {copy('months')}</b>
                            </span>
                        </div>
                    </MetricCard>
                    <MetricCard area="currency" label={copy('currencyImpact')} order={5}>
                        <strong className={cn(styles.amount, styles.bottom, styles.positive)}>12 049 Kč</strong>
                    </MetricCard>
                    <MetricCard area="total" label={copy('total')} order={6}>
                        <strong className={cn(styles.amount, styles.bottom)}>1 358 780 Kč</strong>
                        <span className={styles.clock} aria-hidden="true" />
                    </MetricCard>
                    <MetricCard area="fees" label={copy('fees')} order={7}>
                        <div className={styles.bottom}>
                            <strong className={cn(styles.amount, styles.negative)}>1 365 Kč</strong>
                            <span className={styles.note}>0,12 % {copy('ofInvested')}</span>
                        </div>
                    </MetricCard>
                </div>
                <div className={styles.allocations}>
                    <Allocation
                        title={copy('assetTypes')}
                        labels={['ETF', copy('stocks'), copy('crypto')]}
                        values={['52,2 %', '45,6 %', '2,2 %']}
                    />
                    <Allocation
                        title={copy('currencies')}
                        labels={['USD', 'EUR', 'CZK']}
                        values={['83,6 %', '12,2 %', '4,2 %']}
                    />
                    <Allocation
                        title={copy('countries')}
                        labels={['USA', copy('europe'), copy('asia')]}
                        values={['79,8 %', '16,6 %', '3,6 %']}
                    />
                </div>
            </div>
        </div>
    );
};
